import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowUpRight, BadgeCheck, Building2, ChevronLeft, ChevronRight, ExternalLink, LoaderCircle, PackageOpen, QrCode, ShieldCheck, Sparkles, X } from 'lucide-react';
import { siteAssetBackground, useSiteAsset } from '../content/SiteAssetsContext';
import { useSiteCopy } from '../content/SiteCopyContext';
import type { PartnerProduct } from '../types';
import { HeroPromotionCarousel } from './HeroPromotionCarousel';
import { useExperience } from '../content/ExperienceContext';
import { SiteFooter } from './SiteFooter';
import { BeadParticleField } from './BeadParticleField';

type AuxiliaryDialog = 'qualification' | 'qr' | null;

async function readJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || '商品读取失败');
  return data as T;
}

function productImages(product: PartnerProduct) {
  return product.productImages?.length ? product.productImages : (product.coverImage ? [product.coverImage] : []);
}

function qualificationImages(product: PartnerProduct) {
  return product.qualificationImages?.length ? product.qualificationImages : (product.qualificationImage ? [product.qualificationImage] : []);
}

export function ProductsPage() {
  const copy = useSiteCopy();
  const { locale } = useExperience();
  const heroBackground = useSiteAsset('products.heroBackground');
  const [products, setProducts] = useState<PartnerProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [active, setActive] = useState<PartnerProduct | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [auxiliary, setAuxiliary] = useState<AuxiliaryDialog>(null);
  const [qualificationPreview, setQualificationPreview] = useState<number | null>(null);
  const [currencyRates, setCurrencyRates] = useState<Record<string, number>>({});
  const [currencyEnabled, setCurrencyEnabled] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const auxiliaryCloseRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    readJson<{ products: PartnerProduct[] }>(`/api/products${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`, controller.signal)
      .then((data) => setProducts(Array.isArray(data.products) ? data.products : []))
      .catch((reason) => { if (reason.name !== 'AbortError') setError(reason.message || '商品读取失败'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [locale]);
  useEffect(() => {
    const controller = new AbortController();
    readJson<{ enabled?: boolean; rates: Record<string, number> }>('/api/exchange-rates', controller.signal)
      .then((data) => {
        setCurrencyEnabled(Boolean(data.enabled));
        setCurrencyRates(data.rates || {});
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  const displayPrice = (product: PartnerProduct) => {
    const currency = locale === 'en' ? 'USD' : locale === 'ja' ? 'JPY' : locale === 'ko' ? 'KRW' : 'CNY';
    if (currency === 'CNY' || !currencyEnabled) return product.priceLabel;
    const manual = Number(product.currencyPrices?.[currency]);
    const amount = Number(product.priceAmount || String(product.priceLabel || '').match(/\d+(?:\.\d+)?/)?.[0] || 0);
    const value = manual > 0 ? manual : amount * Number(currencyRates[currency] || 0);
    if (!value) return product.priceLabel;
    const formatted = new Intl.NumberFormat(locale === 'ja' ? 'ja-JP' : locale === 'ko' ? 'ko-KR' : 'en-US', { style: 'currency', currency, maximumFractionDigits: currency === 'JPY' || currency === 'KRW' ? 0 : 2 }).format(value);
    if (!product.priceFrom) return formatted;
    if (locale === 'en') return `From ${formatted}`;
    if (locale === 'ko') return `${formatted}부터`;
    return `${formatted}〜`;
  };

  useEffect(() => {
    if (!active) return;
    const previous = document.activeElement as HTMLElement | null;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (auxiliary) setAuxiliary(null);
        else setActive(null);
        return;
      }
      if (event.key !== 'Tab') return;
      const root = auxiliary ? auxiliaryCloseRef.current?.closest('[role="dialog"]') : closeRef.current?.closest('[role="dialog"]');
      const focusable = [...(root?.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), [tabindex]:not([tabindex="-1"])') || [])];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { last.focus(); event.preventDefault(); }
      else if (!event.shiftKey && document.activeElement === last) { first.focus(); event.preventDefault(); }
    };
    window.addEventListener('keydown', handleKey);
    requestAnimationFrame(() => (auxiliary ? auxiliaryCloseRef : closeRef).current?.focus());
    return () => { window.removeEventListener('keydown', handleKey); if (!auxiliary) previous?.focus?.(); };
  }, [active?.id, auxiliary]);

  async function openDetail(product: PartnerProduct) {
    setActive(product);
    setActiveImage(0);
    setAuxiliary(null);
    setQualificationPreview(null);
    setDetailLoading(true);
    try {
      const data = await readJson<{ product: PartnerProduct }>(`/api/products/${encodeURIComponent(product.id)}${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`);
      setActive(data.product);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '商品详情读取失败');
      setActive(null);
    } finally { setDetailLoading(false); }
  }

  const activeImages = active ? productImages(active) : [];
  const activeQualifications = active ? qualificationImages(active) : [];

  return (
    <main id="primary-content" className="products-page">
      <section className="site-section-hero products-hero" style={siteAssetBackground(heroBackground)}>
        <BeadParticleField />
        <div className="site-section-hero-copy products-hero-copy">
          <span className="products-kicker"><Sparkles size={15} />{copy('products.kicker')}</span>
          <h1>{copy('products.titleLine1')}<em>{copy('products.titleLine2')}</em></h1>
          <p>{copy('products.intro')}</p>
          <div className="products-trust-row"><span><ShieldCheck size={16} />{copy('products.trustAssets')}</span><span><ExternalLink size={16} />{copy('products.trustJump')}</span><span><QrCode size={16} />{copy('products.trustQr')}</span></div>
        </div>
        {heroBackground && <div className="site-hero-media" aria-hidden="true"><img src={heroBackground} alt="" /></div>}
        <HeroPromotionCarousel placement="products" />
      </section>

      <section className="products-library">
        <div className="products-heading page-context-bar">
          <div><strong>{copy('products.sectionTitle')}</strong><small>{copy('products.countTemplate', { count: products.length })}</small></div>
        </div>
        <div className="products-page-statement"><ShieldCheck size={18} /><p><strong>{copy('products.statementTitle')}</strong><span>{copy('products.statement')}</span></p></div>

        {loading ? <div className="products-state"><LoaderCircle className="spin" size={25} /><span>{copy('products.loading')}</span></div>
          : error ? <div className="products-state error"><strong>{copy('products.errorTitle')}</strong><span>{error}</span></div>
            : products.length ? <div className="products-grid">
              {products.map((product) => {
                const image = productImages(product)[0];
                return <article className="product-card" key={product.id}>
                  <button className="product-card-open" type="button" onClick={() => void openDetail(product)} aria-label={`${copy('products.viewDetail')}：${product.name}`}>
                    <div className="product-card-visual">
                      {image ? <img src={image} alt={`${product.name}商品图`} loading="lazy" /> : <span><PackageOpen size={42} /><small>商品图片准备中</small></span>}
                      {product.featured && <b><Sparkles size={13} />合作精选</b>}
                      {product.category && <em>{product.category}</em>}
                    </div>
                    <div className="product-card-copy">
                      <span className="product-merchant"><Building2 size={14} />{product.merchantName || copy('products.defaultMerchant')}</span>
                      <h2>{product.name}</h2>
                      <p>{product.subtitle || copy('products.defaultSubtitle')}</p>
                      <div className="product-card-tags">{product.tags.slice(0, 3).map((tag) => <i key={tag}>{tag}</i>)}</div>
                      <footer><strong>{displayPrice(product)}</strong><span>{copy('products.viewDetail')}<ArrowUpRight size={16} /></span></footer>
                    </div>
                  </button>
                </article>;
              })}
            </div> : <div className="products-state"><PackageOpen size={28} /><strong>{copy('products.emptyTitle')}</strong><span>{copy('products.emptyBody')}</span></div>}
      </section>

      <SiteFooter />

      {active && <div className="product-detail-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setActive(null); }}>
        <article className="product-detail" role="dialog" aria-modal="true" aria-labelledby="product-detail-title">
          <button ref={closeRef} className="product-detail-close" type="button" onClick={() => setActive(null)} aria-label="关闭商品详情"><X size={20} /></button>
          <div className="product-detail-gallery">
            <div className="product-detail-media">
              {activeImages[activeImage] ? <img src={activeImages[activeImage]} alt={`${active.name}商品图 ${activeImage + 1}`} /> : <span><PackageOpen size={52} />暂无商品图</span>}
              {active.featured && <b><Sparkles size={14} />合作精选</b>}
              {activeImages.length > 1 && <div className="product-detail-media-nav"><button type="button" onClick={() => setActiveImage((activeImage - 1 + activeImages.length) % activeImages.length)} aria-label="上一张商品图"><ChevronLeft size={20} /></button><span>{activeImage + 1} / {activeImages.length}</span><button type="button" onClick={() => setActiveImage((activeImage + 1) % activeImages.length)} aria-label="下一张商品图"><ChevronRight size={20} /></button></div>}
            </div>
            {activeImages.length > 1 && <div className="product-detail-thumbs" aria-label="商品图片">
              {activeImages.map((image, index) => <button className={index === activeImage ? 'active' : ''} key={`${index}-${image.slice(-24)}`} type="button" onClick={() => setActiveImage(index)} aria-label={`查看第 ${index + 1} 张商品图`}><img src={image} alt="" /></button>)}
            </div>}
          </div>
          <div className="product-detail-main">
            <span className="product-detail-category">{active.category || copy('products.detailCategory')}</span>
            <h2 id="product-detail-title">{active.name}</h2>
            <p className="product-detail-subtitle">{active.subtitle}</p>
            <div className="product-detail-merchant"><Building2 size={17} /><span><small>合作商家</small><strong>{active.merchantName || copy('products.defaultMerchant')}</strong></span><BadgeCheck size={18} /></div>
            <strong className="product-detail-price">{displayPrice(active)}</strong>
            <div className="product-detail-tags">{active.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            {detailLoading ? <div className="product-detail-loading"><LoaderCircle className="spin" size={18} />正在读取完整资料…</div> : <>
              {active.description && <section className="product-detail-section"><h3>{copy('products.descriptionTitle')}</h3>{active.description.split(/\n+/).filter(Boolean).map((paragraph, index) => <p key={`${index}-${paragraph}`}>{paragraph}</p>)}</section>}
              {active.highlights.length > 0 && <section className="product-detail-section"><h3>{copy('products.highlightsTitle')}</h3><ul>{active.highlights.map((highlight, index) => <li key={`${index}-${highlight}`}><BadgeCheck size={16} />{highlight}</li>)}</ul></section>}
              <div className="product-detail-proof-actions">
                <button type="button" onClick={() => { setQualificationPreview(null); setAuxiliary('qualification'); }}><ShieldCheck size={18} /><span><strong>{copy('products.qualificationButton')}</strong><small>{activeQualifications.length ? `${activeQualifications.length} 张资料` : '暂未上传'}</small></span></button>
                <button type="button" onClick={() => setAuxiliary('qr')}><QrCode size={18} /><span><strong>{copy('products.qrButton')}</strong><small>{active.qrImage ? '打开购买二维码' : '暂未上传'}</small></span></button>
              </div>
              <div className="product-detail-statement"><ShieldCheck size={18} /><p><strong>{copy('products.purchaseStatement')}</strong><span>{active.statement || copy('products.statement')}</span></p></div>
              <div className="product-detail-actions">
                {active.externalUrl ? <a href={active.externalUrl} target="_blank" rel="noopener noreferrer">{copy('products.externalButton')}<ExternalLink size={17} /></a> : <button type="button" onClick={() => setAuxiliary('qr')}>{copy('products.qrButton')}<QrCode size={17} /></button>}
                <small>{copy('products.leaveNote')}</small>
              </div>
            </>}
          </div>
        </article>
      </div>}

      {active && auxiliary && <div className="product-auxiliary-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setAuxiliary(null); }}>
        <section className="product-auxiliary-dialog" role="dialog" aria-modal="true" aria-labelledby="product-auxiliary-title">
          <button ref={auxiliaryCloseRef} className="product-detail-close" type="button" onClick={() => setAuxiliary(null)} aria-label="关闭"><X size={20} /></button>
          <span>{active.merchantName || copy('products.defaultMerchant')}</span>
          <h2 id="product-auxiliary-title">{copy(auxiliary === 'qualification' ? 'products.qualificationTitle' : 'products.qrTitle')}</h2>
          {auxiliary === 'qualification'
            ? activeQualifications.length
              ? qualificationPreview === null
                ? <div className="product-qualification-gallery">{activeQualifications.map((image, index) => <button key={`${index}-${image.slice(-24)}`} type="button" onClick={() => setQualificationPreview(index)}><img src={image} alt={`${active.merchantName || active.name}资质图 ${index + 1}`} /><strong>{active.qualificationNotes?.[index] || `资质资料 ${index + 1}`}</strong><small>点击查看大图</small></button>)}</div>
                : <div className="product-qualification-preview"><button type="button" onClick={() => setQualificationPreview(null)}><ArrowLeft size={16} />返回资质列表</button><img src={activeQualifications[qualificationPreview]} alt={`${active.merchantName || active.name}资质图 ${qualificationPreview + 1}`} /><p>{active.qualificationNotes?.[qualificationPreview] || `资质资料 ${qualificationPreview + 1}`}</p></div>
              : <p>{copy('products.qualificationEmpty')}</p>
            : active.qrImage ? <img className="product-purchase-qr" src={active.qrImage} alt={`${active.name}购买二维码`} /> : <p>{copy('products.qrEmpty')}</p>}
        </section>
      </div>}
    </main>
  );
}
